/**
 * Utilitário para manipulação de datas e horas no fuso horário de Brasília (UTC-3).
 * Garante que o sistema opere corretamente no horário local do Brasil, mesmo em servidores UTC.
 */

export class DateUtil {
    /**
     * Retorna a data e hora atual no fuso de Brasília como ISO String.
     * Exemplo: '2026-03-01T22:15:23.000-03:00'
     */
    static getBrasiliaISOString(): string {
        const now = new Date();
        // Ajuste para UTC-3 (Brasília)
        // Usamos o offset fixo de -3 horas, já que o sistema não precisa lidar com horário de verão no momento
        const offset = -3;
        const brDate = new Date(now.getTime() + (offset * 60 * 60 * 1000));

        // Formata manualmente para garantir o offset no final
        const iso = brDate.toISOString().replace('Z', '-03:00');
        return iso;
    }

    /**
     * Converte uma data (Date ou string ISO) para o formato 'YYYY-MM-DD' de Brasília.
     */
    static toBrasiliaDateString(val: any): string | null {
        if (!val) return null;

        // Se já for uma string YYYY-MM-DD, retorna diretamente para evitar processamento
        if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) return val;

        let date: Date;
        if (val instanceof Date) {
            date = val;
        } else {
            date = new Date(val);
        }

        if (isNaN(date.getTime())) return null;

        // Usamos Intl.DateTimeFormat para extrair a data no fuso de Brasília sem erros de shift
        try {
            const formatter = new Intl.DateTimeFormat('pt-BR', {
                timeZone: 'America/Sao_Paulo',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            });
            const parts = formatter.formatToParts(date);
            const d = parts.find(p => p.type === 'day')?.value;
            const m = parts.find(p => p.type === 'month')?.value;
            const y = parts.find(p => p.type === 'year')?.value;
            return `${y}-${m}-${d}`;
        } catch (e) {
            // Fallback para o método anterior se falhar
            const brDate = new Date(date.getTime() - (3 * 60 * 60 * 1000));
            return brDate.toISOString().split('T')[0];
        }
    }

    /**
     * Retorna um Date objeto ajustado para o fuso de Brasília.
     */
    static getBrasiliaNow(): Date {
        const now = new Date();
        return new Date(now.getTime() - (3 * 60 * 60 * 1000));
    }

    /**
     * Formata uma data para o banco de dados (TIMESTAMPTZ) garantindo o offset BR.
     */
    static toDbTimestamp(val: any): string {
        if (!val) return this.getBrasiliaISOString();

        let date: Date;
        if (val instanceof Date) {
            date = val;
        } else {
            // Tenta normalizar se for DD/MM/YYYY
            if (typeof val === 'string' && val.includes('/')) {
                const parts = val.split('/');
                if (parts.length === 3) {
                    date = new Date(`${parts[2]}-${parts[1]}-${parts[0]}T00:00:00-03:00`);
                } else {
                    date = new Date(val);
                }
            } else {
                date = new Date(val);
            }
        }

        if (isNaN(date.getTime())) return this.getBrasiliaISOString();

        // Se já tiver offset na string ou for um Date criado localmente, 
        // precisamos garantir que ele saia com o offset de Brasília para o Supabase
        const iso = date.toISOString();
        if (iso.endsWith('Z')) {
            // Verificamos se o input original era apenas uma data (sem T)
            // Se for string e não tiver "T", tratamos como início do dia local
            if (typeof val === 'string' && !val.includes('T')) {
                const clean = val.includes(' ') ? val.split(' ')[0] : val;
                return `${clean}T00:00:00.000-03:00`;
            }
            // Caso contrário, ajustamos o timestamp para Brasília
            const brTime = date.getTime() - (3 * 60 * 60 * 1000);
            return new Date(brTime).toISOString().replace('Z', '-03:00');
        }

        return iso;
    }

    /**
     * Retorna a quantidade exata de semanas no mês da data fornecida (dias / 7).
     */
    static getWeeksInMonth(date: Date): number {
        const year = date.getFullYear();
        const month = date.getMonth();
        const days = new Date(year, month + 1, 0).getDate();
        return days / 7;
    }

    /**
     * Retorna o intervalo da semana atual (Segunda-feira a Domingo) em formato ISO.
     */
    static getWeekRange(date: Date = new Date()): { start: string, end: string } {
        const day = date.getDay(); // 0 is Sunday, 1 is Monday...
        const diffToMon = date.getDate() - day + (day === 0 ? -6 : 1);
        const monday = new Date(date.setDate(diffToMon));
        monday.setHours(0, 0, 0, 0);

        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);
        sunday.setHours(23, 59, 59, 999);

        return {
            start: monday.toISOString(),
            end: sunday.toISOString()
        };
    }

    /**
     * Retorna o intervalo do mês da data fornecida (Primeiro dia a Último dia) em formato ISO.
     */
    static getMonthRange(date: Date = new Date()): { start: string, end: string } {
        const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
        firstDay.setHours(0, 0, 0, 0);

        const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        lastDay.setHours(23, 59, 59, 999);

        return {
            start: firstDay.toISOString(),
            end: lastDay.toISOString()
        };
    }
}

